import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import DataTable from '../../components/Common/DataTable';
import api from '../../services/api';

const formatAction = (value) => {
  if (!value) return '-';
  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const Logs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(search ? { search } : {})
      };
      const response = await api.get('/admin/logs', { params });
      if (response.data.success) {
        setLogs(response.data.data);
        setTotal(response.data.total);
      }
    } catch (err) {
      setError('Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // Other admins can be acting at the same time — poll quietly in the
  // background so new entries show up without a manual refresh. No spinner,
  // no error toast on a missed poll.
  useEffect(() => {
    const interval = setInterval(() => {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(search ? { search } : {})
      };
      api.get('/admin/logs', { params })
        .then(response => {
          if (response.data.success) {
            setLogs(response.data.data);
            setTotal(response.data.total);
          }
        })
        .catch(err => console.error('Background log refresh failed:', err.message));
    }, 15000);
    return () => clearInterval(interval);
  }, [page, rowsPerPage, search]);

  const columns = [
    {
      key: 'date',
      label: 'Date & Time',
      minWidth: 160,
      render: (value) => {
        const formatted = new Date(value).toLocaleString('en-GB', {
          day: '2-digit', month: '2-digit', year: '2-digit',
          hour: '2-digit', minute: '2-digit',
          timeZone: 'UTC'
        });
        return (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography variant="body2">{formatted}</Typography>
            <Typography variant="caption" color="text.secondary">GMT</Typography>
          </Box>
        );
      }
    },
    {
      key: 'task',
      label: 'Action',
      minWidth: 130,
      render: (value) => <Typography variant="body2">{formatAction(value)}</Typography>
    },
    {
      key: 'by_who',
      label: 'User',
      minWidth: 180,
      render: (value) => <Typography variant="body2">{value === 'Admin' ? 'UNFTP' : value}</Typography>
    },
    {
      key: 'ip_address',
      label: 'IP Address',
      minWidth: 120,
      render: (value) => value || '-'
    },
    {
      key: 'details',
      label: 'Details',
      render: (value) => value || '-'
    }
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header: title + count on left, search on right */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography variant="h4" fontWeight={600}>Logs</Typography>
          {total > 0 && (
            <Typography variant="h6" color="text.secondary" fontWeight={400}>
              ({total.toLocaleString()})
            </Typography>
          )}
        </Box>
        <TextField
          size="small"
          placeholder="Search logs..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ width: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => { setSearch(''); setPage(0); }}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Table fills remaining height, scrolls internally */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <DataTable
          columns={columns}
          data={logs}
          loading={loading}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          filters={[]}
          filterValues={{}}
          onFilterChange={undefined}
          emptyMessage="No logs found"
          showSearch={false}
          showRefresh={false}
          showExport={false}
          sortable={false}
          rowsPerPageOptions={[100, 50, 25]}
          hideToolbar={true}
          fullHeight={true}
          dense={true}
        />
      </Box>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Logs;
